/**
 * The launch-monitor driving range, in passthrough.
 *
 * Everything generic to an MR launch-monitor page — anchoring, the window mask,
 * the bridge, the menus, the shot plumbing, the chase-cam TV — lives in
 * `examples/shared/mrStage.ts`. What's left here is the range itself: a flat
 * fairway with yardage lines, a mountain backdrop, and four target greens.
 */
import {
  THREE,
  app,
  CourseLight,
  GolfBall,
  UnitConversions,
  YardageLinesMaterial,
  generateSetupData,
  ContactShadow,
  FlagStick,
  SkyDome,
  MeshStandardNodeMaterial,
} from '@opengolfsim/fuse';
import type { InfoPage } from '@opengolfsim/fuse';
import rangeMtnsModel from '../range/models/rangeMtns.glb?url';
import fairwayTexture from '../range/textures/gen_fairway_tex.png?url';
import fairwayMap from '../range/textures/gen_fairway_map.png?url';
import { MrStage, statusEl, sunColor } from '../shared/mrStage';

const mountainColor = new THREE.Color('#687e80');
const hashMarks = [50, 100, 150, 200, 250, 300];

/**
 * Practice preset the MR page starts on. A short chip is what's useful on an
 * indoor mat; set here rather than by reordering SHOT_PRESETS, which is public
 * API and also maps the desktop number keys by array position.
 */
const DEFAULT_PRESET = 'Chip';

/** Range scenery, kept off the stage because none of it is generic. */
const range: {
  ground?: THREE.Mesh,
  groundLines?: THREE.Mesh,
  mountain?: THREE.Object3D,
  yardageLines?: YardageLinesMaterial,
  lightGroup?: CourseLight,
  skyDome?: SkyDome,
  flagSticks: FlagStick[],
} = { flagSticks: [] };

/**
 * Orient a flag downwind and scale its cloth force. The cloth sim blows along
 * the flagstick's LOCAL +Z, so yawing the whole stick points the flutter
 * downwind; rotation.y = −rad maps local +Z onto the wind vector
 * (−sin rad, 0, cos rad).
 */
function setFlagWind(stick: FlagStick, mph: number, deg: number) {
  const mps = UnitConversions.milesPerHourToMetersPerSecond(mph);
  stick.object.rotation.y = -THREE.MathUtils.degToRad(deg);
  stick.windForce.set(0, 0, mps > 0.5 ? 0.35 + mps * 0.28 : 0);
}

function applyWind(stage: MrStage) {
  stage.applyWind();
  range.flagSticks.forEach(stick => setFlagWind(stick, stage.windMph, stage.windDeg));
}

async function createGroundPlane(stage: MrStage) {
  const rangeWidth = 500;
  const rangeHeight = 700;
  const widthRatio = rangeHeight / rangeWidth;
  const grassScale = 100;

  const textureLoader = new THREE.TextureLoader();
  const grassTexture = textureLoader.load(fairwayTexture);
  grassTexture.wrapS = THREE.RepeatWrapping;
  grassTexture.wrapT = THREE.RepeatWrapping;
  grassTexture.repeat.set(grassScale, grassScale * widthRatio);
  grassTexture.colorSpace = THREE.SRGBColorSpace;
  grassTexture.anisotropy = stage.renderer.getMaxAnisotropy() || 1;

  const grassNormalMap = textureLoader.load(fairwayMap);
  grassNormalMap.wrapS = THREE.RepeatWrapping;
  grassNormalMap.wrapT = THREE.RepeatWrapping;
  grassNormalMap.repeat.set(grassScale, grassScale * widthRatio);
  grassNormalMap.colorSpace = THREE.SRGBColorSpace;
  grassNormalMap.anisotropy = stage.renderer.getMaxAnisotropy() || 1;

  const floorMaterial = new THREE.MeshStandardMaterial({
    name: 'floor',
    map: grassTexture,
    normalMap: grassNormalMap,
    roughness: 1,
    metalness: 0,
  });

  const floorGeometry = new THREE.PlaneGeometry(rangeWidth, rangeHeight, 50, 70);

  range.ground = new THREE.Mesh(floorGeometry, floorMaterial);
  range.ground.rotation.x = -Math.PI / 2;
  range.ground.position.y = 0;
  range.ground.position.z = 140;
  range.ground.userData.surface = 'fairway';
  stage.worldRoot.add(range.ground);

  // The yardage-lines clone (opaque, carries the same fairway map) fully
  // covers the base plane, which exists for physics only in this app.
  range.ground.visible = false;

  range.groundLines = range.ground.clone();
  range.groundLines.position.y = 0.001;
  range.groundLines.visible = true;
  stage.worldRoot.add(range.groundLines);

  const downrange = new THREE.Vector3(0, 0, 1);
  const distances = [...hashMarks].map(val =>
    stage.setupData.units === 'imperial' ? UnitConversions.yardsToMeters(val) : val);

  range.yardageLines = new YardageLinesMaterial(
    range.groundLines,
    stage.startPoint,
    downrange,
    distances,
    {
      lineWidth: 0.5,
      lineLength: 50,
      maxTextureSize: stage.renderer.getMaxTextureSize(),
      labels: hashMarks,
      lineColor: [1, 1, 1, 0.5],
      feather: 0.2,
      texelsPerMeter: 40,
    }
  );

  await loadMountain(stage, grassTexture, grassNormalMap);
}

async function loadMountain(stage: MrStage, grassTexture: THREE.Texture, grassNormalMap: THREE.Texture) {
  range.mountain = await stage.meshLoader.load(rangeMtnsModel, true);
  if (!range.mountain) {
    console.warn('Unable to load mountain mesh!');
    return;
  }

  // Node material, not THREE.MeshStandardMaterial: WorldMask.applyToMaterial
  // requires isNodeMaterial, which the classic classes don't set — so the
  // mountain mask below was being silently skipped with a console warning.
  // (The ground escapes this only because YardageLinesMaterial replaces its
  // material with a MeshStandardNodeMaterial of its own.)
  const mountainMaterial = new MeshStandardNodeMaterial({
    map: grassTexture,
    normalMap: grassNormalMap,
    roughness: 1,
    color: mountainColor,
    normalScale: new THREE.Vector2(0, 0.5),
    metalness: 0,
  });

  // @ts-expect-error single-mesh GLB
  range.mountain.material = mountainMaterial;
  range.mountain.position.set(0, -12, 900);
  range.mountain.scale.set(20, 20, 20);
  stage.worldRoot.add(range.mountain);
}

/** Help panel copy. Rebuilt on each redraw so the status line stays live. */
function helpPages(stage: MrStage): InfoPage[] {
  return [
    {
      title: 'Launch monitor setup',
      body: [
        `Status:  ${stage.launchMonitorState().text}`,
        `Wind:  ${stage.windLabel()}  (?wind=mph,deg — 0° tail · 180° into · 90° L→R)`,
        `Bridge:  ${stage.bridgeUrl}${stage.bridgeUrlNote()}`,
        '',
        '• Run the FUSE bridge on the Pi that has your launch monitor.',
        '• Point your connector at that Pi, port 3111 (OpenFlight/OGS) or 921 (GSPro).',
        '• Open http://<pi-ip>:8080 in the Quest browser — it redirects here, and',
        '  the bridge is found automatically because it served this page.',
        '',
        'Other setups: FUSE Link on the headset serves http://localhost:9210/range-mr/.'
          + ' A bridge elsewhere on the LAN needs ?bridge=wss://<host>:8443/ws'
          + ' (?bridge=default forgets a saved one).',
        '',
        'The range shows through a window framed by your walls and ceiling (needs'
          + ' Space Setup). ?window=off for the infinite view, ?window=dist,left,right,top'
          + ' to size it by hand.',
        '',
        'Passthrough feels laggy? Bright, even lighting is the biggest fix (dim rooms'
          + ' lengthen camera exposure). Then keep frames held: ?hz=90 pins the refresh'
          + ' rate, ?fbscale=0.85 buys GPU headroom, ?perf=1 shows frame times.',
      ],
    },
    {
      title: 'Controls — bare hands',
      body: [
        '• Look at your ball and pinch to place the tee. Look downrange and pinch again to aim.',
        '• Pinch to fire a practice shot. Thumb to middle finger cycles the club.',
        '• Hold a pinch two seconds to re-place the tee.',
        '• Palm toward you plus a glance opens the wrist menu; poke it with your'
          + ' other index finger.',
        '',
        'Hand input pauses while you grip and swing, so a real swing never fires'
          + ' anything.',
        '',
        'Practice pinches lock out once a monitor sends a real shot (?testshots=1 keeps them).',
      ],
    },
    {
      title: 'Controls — controller',
      body: [
        'Everything works with a controller in hand, and you can switch between'
          + ' the two at will — pick one up or set it down, nothing needs telling.',
        '',
        '• Point at the floor by your ball and pull the trigger to place the tee.'
          + ' A beam shows where it will land. Then point downrange and pull again'
          + ' to set the target line.',
        '• Trigger fires a practice shot once the tee is down.',
        '• A/X cycles the practice shot preset.',
        '• B/Y opens this menu — point at a row with either controller and pull the'
          + ' trigger to click it. B/Y again closes it.',
        '• Hold the trigger two seconds, or hold the grip button, to re-place the tee.',
        '',
        'Pointing only counts when the controller is angled down at the floor, so a'
          + ' controller you have set down beside the mat can never steal the tee'
          + ' from your gaze.',
      ],
    },
  ];
}

const stage = new MrStage({
  title: 'Range',
  defaultPreset: DEFAULT_PRESET,
  ballWaitTime: 400, // range flow: ~no dead time between shots

  async buildContent(stage) {
    range.lightGroup = new CourseLight({
      color: sunColor,
      ambient: { enabled: true, intensity: 1.0 },
      directional: { enabled: true, intensity: 1.1 },
    });
    stage.scene.add(range.lightGroup);

    await createGroundPlane(stage);

    stage.golfBall = new GolfBall(stage.worldRoot, {
      setupData: stage.setupData,
      clearTrail: 'start',
      groundMeshes: [range.ground!],
      waitTime: 400,
      trailOptions: {
        layer: 0,                // layers 1/2 are per-eye in XR
        cameraFadeNear: 0.25,    // the head stands at the tee — keep launch visible
        cameraFadeFar: 0.6,      //   (a chip's whole arc is only ~9 m long)
        lineWidth: 0.045,        // survives bright passthrough at 100+ m
        color: 0xff7733,
        qualityLevel: 0,
        sampleEvery: 2,          // Low would sample every 10th frame: ~7 points per chip
        fadeLength: 0.6,         // 2 m of end-fade would eat a quarter of a chip arc
        // Mandatory in XR: the cameraPosition built-in throws under three r185's
        // ArrayCamera, which silently drops MeshLine's vertexNode and collapses
        // the whole ribbon to zero-area triangles. See BallTrailOptions.viewerFade.
        viewerFade: 'uniform',
      },
    });

    stage.contactShadow = new ContactShadow();
    stage.worldRoot.add(stage.contactShadow.object);

    if (range.groundLines) {
      stage.worldMask!.applyToMaterial(range.groundLines.material as THREE.Material, { ground: true });
    }
    // Mountains: the portal term is ≡1 at 900 m, but masking them keeps the
    // optional side-wall/window limits effective if enabled during tuning
    const mountainMaterial = (range.mountain as THREE.Mesh | undefined)?.material;
    if (mountainMaterial && !Array.isArray(mountainMaterial)) {
      stage.worldMask!.applyToMaterial(mountainMaterial, { ground: false });
    }

    // Sky as a masked dome rather than scene.background / backgroundNode: a
    // screen-space background paints every pixel, which in passthrough erases
    // the real room everywhere, portal included. Running the dome through the
    // same mask makes the sky end at the MR boundary — present downrange,
    // absent behind the player and directly overhead, where the real ceiling
    // should still show through.
    // No sunDirection override: the dome computes its gradient from local
    // position, so the sun lives in worldRoot/sim space (+Z downrange) and its
    // default is up-and-downrange. Feeding it the CourseLight direction would be
    // wrong — that light sits in reference space, so the sun would slide sideways
    // every time the tee is re-anchored.
    range.skyDome = new SkyDome({ sunColor });
    stage.worldRoot.add(range.skyDome.object);
    stage.worldMask!.applyToMaterial(range.skyDome.material, { ground: false });

    // Range targets: greens with flags at real distances down the aim line, so
    // there is somewhere to AIM instead of an empty fairway. Built in sim
    // coordinates under worldRoot (tee origin, +Z downrange), masked like the
    // rest of the virtual world so the window/portal clips them correctly.
    // No golf cup: its shader uses the cameraPosition built-in, which is broken
    // in r185 XR (see BallTrailOptions.viewerFade) — and a cup is invisible at
    // 50+ yards anyway.
    const targets = new THREE.Group();
    targets.name = 'RangeTargets';
    for (const yards of [50, 100, 150, 200]) {
      const z = UnitConversions.yardsToMeters(yards);
      const greenMaterial = new MeshStandardNodeMaterial({
        color: 0x2f7a45, // a step darker than the fairway so the green reads
        roughness: 0.85,
        metalness: 0,
      });
      const green = new THREE.Mesh(
        new THREE.CircleGeometry(4 + yards * 0.02, 40).rotateX(-Math.PI / 2),
        greenMaterial
      );
      green.position.set(0, 0.006, z); // above the yardage-lines clone
      green.renderOrder = 1;
      targets.add(green);
      stage.worldMask!.applyToMaterial(greenMaterial, { ground: true });

      const stick = new FlagStick(new THREE.Vector3(0, 0, z), `${yards}`);
      // Node materials so the mask can clip them at the window edges; the
      // originals are classic MeshStandardMaterial, which the mask must skip
      stick.flag.material = new MeshStandardNodeMaterial({ color: 0xd23b2f, side: THREE.DoubleSide });
      stick.stick.material = new MeshStandardNodeMaterial({ color: 0xf2f2f2, roughness: 0.4, metalness: 0.3 });
      stage.worldMask!.applyToMaterial(stick.flag.material as THREE.Material, { ground: false });
      stage.worldMask!.applyToMaterial(stick.stick.material as THREE.Material, { ground: false });
      setFlagWind(stick, stage.windMph, stage.windDeg);
      range.flagSticks.push(stick);
      targets.add(stick.object);
    }
    stage.worldRoot.add(targets);
  },

  helpPages,

  wristButtons: stage => [
    // Two of the eight slots do double duty rather than growing the panel: the
    // button grid's height is baked at construction, and a taller panel on the
    // wrist is harder to poke, not easier.
    { id: 'action', label: () => 'Practice shot' },
    { id: 'reanchor', label: () => 'Re-place tee' },
    { id: 'units', label: () => `Units: ${stage.setupData.units === 'metric' ? 'M' : 'YD'}` },
    { id: 'tv', label: () => `TV: ${stage.tvMode === 'off' ? 'off' : 'on'}` },
    // Takes the slot the duplicate perf toggle used to hold — the grid's height
    // is baked at construction, so a ninth button is not available, and frame
    // timing is a tuning knob that survives fine on the controller menu.
    { id: 'mode', label: () => 'Play a course' },
    { id: 'help', label: () => 'Setup / help' },
    { id: 'reload', label: () => 'Reload page' },
    { id: 'exit', label: () => 'Exit MR' },
  ],

  onSelect(id, stage) {
    if (id !== 'mode') return false;
    // Sibling page, not a mode flag: the course is a different world, a
    // different ball parent and a 58 MB download, none of which belongs in the
    // range's memory footprint when nobody has asked for it.
    stage.leaveTo('../course-mr/');
    return true;
  },

  menuSections: stage => [
    {
      id: 'play',
      title: 'Play',
      items: [
        { id: 'action', label: () => 'Practice shot' },
        { id: 'mode', label: () => 'Play a course', value: () => 'Mountain Vista' },
        {
          id: 'club-next',
          prevId: 'club-prev',
          label: () => 'Shot preset',
          value: () => stage.presetName,
        },
        { id: 'reanchor', label: () => 'Re-place tee' },
      ],
    },
    {
      id: 'setup',
      title: 'Setup',
      items: [
        {
          id: 'units',
          label: () => 'Units',
          value: () => stage.setupData.units === 'metric' ? 'Metric' : 'Yards',
        },
      ],
    },
    {
      id: 'display',
      title: 'Display',
      items: [
        { id: 'tv', label: () => 'Shot TV', value: () => stage.tvMode === 'off' ? 'Off' : 'On' },
        { id: 'perf', label: () => 'Frame timing', value: () => stage.perfHud ? 'On' : 'Off' },
      ],
    },
  ],

  onTeePose({ position, yaw }) {
    const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    range.yardageLines?.setFrame(position, position.clone().add(forward));
  },

  onSessionEnd(stage) {
    range.yardageLines?.setFrame(stage.startPoint, stage.aimPoint);
  },

  setupNextShot(stage) {
    stage.golfBall?.reset(stage.aimPoint, stage.startPoint);
    if (stage.currentPlayer) {
      app.sendPlayerUpdate(stage.currentPlayer, stage.startPoint.toArray());
    }
  },

  onFrame(delta, stage, _head, sessionVisible) {
    // Flag cloth — only while the virtual world is actually on show
    if (sessionVisible && stage.worldRoot.visible) {
      for (const stick of range.flagSticks) stick.update(delta);
    }
  },
});

async function preLoad(setupData: OpenGolfSim.SetupData) {
  await MrStage.loadFonts();
  stage.setupData = setupData;
  stage.readParams();
  await stage.build(setupData);

  if (stage.simMode) stage.applySimPreview();
  applyWind(stage);
  stage.connectBridge();
  await stage.start();
}

/** Any setup failure must surface — a blank page in a headset is undebuggable. */
function startApp(setupData: OpenGolfSim.SetupData) {
  preLoad(setupData).catch(error => {
    console.error('FUSE MR failed to start', error);
    document.body.style.opacity = '1';
    if (statusEl()) statusEl()!.textContent = `Failed to start: ${error?.message ?? error}`;
  });
}

let started = false;
function startOnce(setupData: OpenGolfSim.SetupData) {
  if (started) return;
  started = true;
  startApp(setupData);
}

app.initialize(() => {
  if (app.appType === 'web') {
    startOnce(generateSetupData(1));
  } else {
    // Embedded (webview/iframe) hosts must send a setup message; don't hang
    // forever on a blank page if it never arrives
    setTimeout(() => {
      if (!started) {
        console.warn('No setup message received — starting with defaults');
        startOnce(generateSetupData(1));
      }
    }, 4000);
  }
});

app.on('setup', payload => {
  if (!payload?.setupData) throw new Error('No setupData received in setup event!');
  startOnce(payload.setupData as OpenGolfSim.SetupData);
});
